import os from 'node:os';
import readline from 'node:readline';
import { spawnSync } from 'node:child_process';
import { logger } from '../utils/logger.js';
import {
  cancelCliAuthSession,
  openBrowser,
  resolvePlatformAuthBrowserUrl,
  startCliAuthSession,
  waitForCliAuthCompletion,
} from '../utils/cli-auth-api.js';
import { saveCliGitAuth } from '../utils/cli-auth-storage.js';
import { consumeBootstrapLogin, type LoginOptions } from './login.js';
import { parseBootstrapCodeInput, resolvePlatformEndpoint } from './init.js';

export interface GitLoginOptions extends LoginOptions {
  config?: boolean;
  helperCommand?: string;
  gitRunner?: (args: string[]) => { status: number | null; stdout: string; stderr: string };
  fetchImpl?: typeof fetch;
}

const DEFAULT_GIT_HELPER_COMMAND = '!npx -y @standardagents/cli@latest git-credential';

// Must mirror the supported-host checks in git-credential.ts.
export const STANDARD_AGENTS_GIT_CREDENTIAL_URL_PATTERNS = [
  'https://standardagents.ai',
  'https://*.standardagents.ai',
  'https://standardagentbuilder.com',
  'https://*.standardagentbuilder.com',
  'https://artifacts.cloudflare.net',
  'https://*.artifacts.cloudflare.net',
  'https://artifacts.cloudflare.com',
  'https://*.artifacts.cloudflare.com',
  'https://git.cloudflare.com',
];

function prompt(question: string): Promise<string> {
  const rl = readline.createInterface({
    input: process.stdin,
    output: process.stdout,
  });

  return new Promise((resolve) => {
    rl.question(question, (answer) => {
      rl.close();
      resolve(answer.trim());
    });
  });
}

function defaultGitRunner(args: string[]): { status: number | null; stdout: string; stderr: string } {
  const result = spawnSync('git', args, { encoding: 'utf8' });
  return {
    status: result.status,
    stdout: result.stdout || '',
    stderr: result.stderr || '',
  };
}

function runGit(
  args: string[],
  runner: NonNullable<GitLoginOptions['gitRunner']>,
): { stdout: string; stderr: string } {
  const result = runner(args);
  if (result.status !== 0) {
    throw new Error(result.stderr.trim() || `git ${args.join(' ')} failed`);
  }
  return result;
}

// `git config --get-all` exits 1 with empty stderr when the key simply has
// no values — normal on a machine with no credential helper configured.
// Only a non-zero status WITH stderr is a real git failure.
function readGlobalConfigValues(
  runner: NonNullable<GitLoginOptions['gitRunner']>,
  key: string,
): string[] | null {
  const result = runner(['config', '--global', '--get-all', key]);
  if (result.status !== 0) {
    if (result.stderr.trim()) {
      throw new Error(result.stderr.trim());
    }
    return null;
  }
  if (result.stdout === '') return [];
  return result.stdout.replace(/\r?\n$/, '').split(/\r?\n/);
}

function isStandardAgentsHelper(value: string, helperCommand: string): boolean {
  const trimmed = value.trim();
  return trimmed === helperCommand
    || (trimmed.includes('@standardagents/cli') && trimmed.includes('git-credential'));
}

export function configureStandardAgentsGitCredentials(options: GitLoginOptions = {}): {
  helperCommand: string;
  helperAdded: boolean;
} {
  const runner = options.gitRunner ?? defaultGitRunner;
  const helperCommand = options.helperCommand?.trim() || process.env.STANDARD_AGENTS_GIT_HELPER || DEFAULT_GIT_HELPER_COMMAND;

  // Each Standard Agents host gets an empty reset entry followed by our
  // helper, making it the ONLY helper for these hosts. Without the reset, a
  // system-level helper (e.g. Xcode's osxkeychain) answers first and caches
  // the short-lived Artifacts token; once it expires the cache keeps serving
  // it and pushes 403 forever, because git only re-asks helpers on HTTP 401.
  // Other hosts keep the user's normal helper chain.
  let helperAdded = false;
  const desired = ['', helperCommand];
  for (const pattern of STANDARD_AGENTS_GIT_CREDENTIAL_URL_PATTERNS) {
    const key = `credential.${pattern}.helper`;
    const existing = readGlobalConfigValues(runner, key);
    if (existing && existing.length === desired.length && existing.every((value, i) => value === desired[i])) {
      continue;
    }
    if (existing !== null) {
      runGit(['config', '--global', '--unset-all', key], runner);
    }
    runGit(['config', '--global', '--add', key, ''], runner);
    runGit(['config', '--global', '--add', key, helperCommand], runner);
    helperAdded = true;
  }

  // Older git-login releases installed the helper as an unscoped global
  // credential.helper; drop those entries (never the user's own helpers) so
  // unrelated hosts stop spawning npx on every credential fill.
  const generic = readGlobalConfigValues(runner, 'credential.helper') ?? [];
  for (const value of new Set(generic.filter((entry) => isStandardAgentsHelper(entry, helperCommand)))) {
    runner(['config', '--global', '--unset-all', '--fixed-value', 'credential.helper', value]);
  }

  runGit(['config', '--global', 'credential.useHttpPath', 'true'], runner);

  return { helperCommand, helperAdded };
}

async function completeBrowserGitLogin(options: GitLoginOptions, endpoint: string): Promise<ReturnType<typeof consumeBootstrapLogin>> {
  let authAppUrl = resolvePlatformAuthBrowserUrl(endpoint);
  let sessionId: string | null = null;
  let wsUrl: string | null = null;
  let wsExpiresAt = 0;

  try {
    // purpose 'auth' sends the browser to the platform's dedicated /cli-auth
    // page, which completes the handshake instantly for signed-in users —
    // no project-onboarding wizard.
    const started = await startCliAuthSession(endpoint, { purpose: 'auth' });
    sessionId = started.session_id;
    authAppUrl = started.auth_url;
    wsUrl = started.ws_url;
    wsExpiresAt = started.expires_at;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to start realtime auth session';
    logger.warning(`${message} Switching to manual bootstrap command entry.`);
  }

  logger.log(`Auth app: ${authAppUrl}`);
  if (options.open !== false) {
    const opened = openBrowser(authAppUrl);
    if (opened) {
      logger.info('Opened browser for authentication.');
    } else {
      logger.warning('Could not automatically open browser. Open the URL above manually.');
    }
  }

  if (wsUrl && wsExpiresAt > 0) {
    try {
      const completion = await waitForCliAuthCompletion(wsUrl, wsExpiresAt);
      return consumeBootstrapLogin({ ...options, endpoint, bootstrap: completion.bootstrapCode });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Realtime auth handoff failed';
      logger.warning(`${message} Enter bootstrap code manually to continue.`);
    }
  }

  if (!process.stdin.isTTY) {
    throw new Error('Non-interactive shell detected. Run: npx @standardagents/cli git-login --bootstrap <code>');
  }

  const input = await prompt('Bootstrap code or command: ');
  const bootstrapCode = parseBootstrapCodeInput(input);
  if (!bootstrapCode) {
    if (sessionId) {
      await cancelCliAuthSession(endpoint, sessionId);
    }
    throw new Error('No bootstrap code provided. Run: npx @standardagents/cli git-login --bootstrap <code>');
  }

  if (sessionId) {
    await cancelCliAuthSession(endpoint, sessionId);
  }
  return consumeBootstrapLogin({ ...options, endpoint, bootstrap: bootstrapCode });
}

export async function gitLogin(options: GitLoginOptions = {}): Promise<void> {
  const endpoint = resolvePlatformEndpoint({ endpoint: options.endpoint });
  logger.log('');
  logger.info('Starting Standard Agents Git authentication...');
  logger.log(`Platform API: ${endpoint}`);

  const state = options.bootstrap?.trim()
    ? await consumeBootstrapLogin({ ...options, endpoint, bootstrap: options.bootstrap.trim() })
    : await completeBrowserGitLogin(options, endpoint);

  if (!state.session_cookie) {
    throw new Error('Platform login did not return a session cookie for Git authentication.');
  }

  // Sessions expire in days; API keys live until revoked. Minting a key here
  // makes git-login a once-per-machine event — future `git push` credential
  // fills authenticate with the key instead of a decaying browser session.
  const apiKey = await mintGitAccessApiKey(endpoint, state.session_cookie, options.fetchImpl);
  if (!apiKey) {
    logger.warning('Could not mint a long-lived API key — falling back to the browser session (expires in a few days).');
  }

  const record = saveCliGitAuth({
    endpoint,
    sessionCookie: state.session_cookie,
    apiKey,
    account: state.account,
    user: state.user,
  });

  let configured: ReturnType<typeof configureStandardAgentsGitCredentials> | null = null;
  if (options.config !== false) {
    configured = configureStandardAgentsGitCredentials(options);
  }

  logger.log('');
  logger.success('Git authentication configured.');
  logger.log(`Account: ${record.account_id}${record.account_slug ? ` (${record.account_slug})` : ''}`);
  logger.log(`User: ${record.user_email}`);
  if (configured) {
    logger.log(`Credential helper: ${configured.helperCommand}${configured.helperAdded ? '' : ' (already installed)'}`);
  }
  if (record.api_key) {
    logger.log('Credentials: long-lived API key (no expiry — revoke from the dashboard to sign out)');
  }
  logger.log('');
}

// Mint a long-lived account API key labeled for this machine so git pushes
// keep working after the browser session expires. Failure is non-fatal — the
// session cookie still works, it just decays.
async function mintGitAccessApiKey(
  endpoint: string,
  sessionCookie: string,
  fetchImpl: typeof fetch = fetch,
): Promise<string | null> {
  try {
    const label = `git (${os.hostname()})`;
    const response = await fetchImpl(`${endpoint}/api-keys`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Cookie: sessionCookie,
      },
      body: JSON.stringify({ label }),
    });
    if (!response.ok) return null;
    const body = (await response.json().catch(() => null)) as { key?: string } | null;
    return typeof body?.key === 'string' && body.key.length > 0 ? body.key : null;
  } catch {
    return null;
  }
}
